import React from "react";
import { Divider } from "../components/divider/divider.tsx";
import { TopBar } from "./top-bar.tsx";
import { PreviewBanner } from "./preview-banner.tsx";
import { PropertyDetailsHero } from "./property-details-hero/hero/property-details-hero.tsx";
import { MainPropertyInfo } from "./main-property-info.tsx";
import { KeyFeatures } from "./key-features.tsx";
import { MarketedBy } from "./marketed-by.tsx";
import { Contact, hasContact } from "./contact.tsx";
import { Utilities, hasUtilities } from "./utilities.tsx";
import { RoomsDescription } from "./rooms-description.tsx";
import { MoreInformation } from "./more-information.tsx";
import { BookViewing } from "./book-viewing.tsx";
import { SchoolCatchments } from "./school-catchments.tsx";
import { DistrictStats } from "./district-stats.tsx";
import { Calculators } from "./calculators/calculators.tsx";
import { RecentlyViewedProperties } from "./recently-viewed-properties.tsx";
import { useRecentlyViewedProperties } from "./use-recently-viewed-properties.ts";
import { LinksTable, hasLinks } from "./links-table.tsx";
import { PropertyReferencePrintSection } from "./property-reference-print-section.tsx";
import { TextSection } from "../components/text-section.tsx";
import { FontStyles } from "../utils/font-styles.ts";
import { usePropertyDetailsContext } from "./contexts/property-details-context.tsx";
import { GoogleAd } from "./google-ad.tsx";

const Container: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <div className={`flex flex-col gap-6 md:gap-8 ${className ?? ""}`.trim()}>{children}</div>
);

export const PropertyDetailsScreen: React.FC = () => {
  const context = usePropertyDetailsContext();
  const {
    price,
    schools,
    description,
    features,
    rooms,
    hrLink,
    scheduleLink,
    digitalBrochureLink,
    propertyId,
    agentPhone,
    districtStatsLocation,
    averageSellingPrice,
    medianDaysToSell,
    mostPopularPropertyType,
    preview,
    rental,
  } = context;

  const numericPrice = typeof price === "number" ? price : undefined;
  const hasSchools = (schools?.length ?? 0) > 0;
  const hasKeyFeatures = (features?.length ?? 0) > 0;
  const hasRooms = (rooms?.length ?? 0) > 0;
  const hasMoreInformation = !!(hrLink ?? scheduleLink ?? digitalBrochureLink);
  const hasDistrictStats = !!(
    districtStatsLocation &&
    averageSellingPrice != null &&
    medianDaysToSell != null &&
    mostPopularPropertyType
  );
  const hasUtilitiesSection = hasUtilities(context);
  const hasContactSection = hasContact(context);
  const hasLinksSection = hasLinks(context);
  const recentlyViewed = useRecentlyViewedProperties();
  const hasBookViewing = !!(propertyId ?? agentPhone);
  // An expired link shows the banner alone; a stale one still shows the record it has,
  // alongside the "changes are not visible yet" message.
  const showPropertyDetails = !preview || preview.status !== "Expired";

  return (
    <div className="w-full">
      {preview ? <PreviewBanner message={preview.message} lastUpdatedDate={preview.lastUpdatedDate} /> : <TopBar />}
      {showPropertyDetails && (
        <div className="flex flex-col items-center md:px-6 ">
          <div className="w-full max-w-espc-max-content flex flex-col gap-6 md:gap-8 lg:gap-6.5 mx-auto md:pt-4.5 pb-12">
            <PropertyDetailsHero />
            <div className="flex flex-col px-3.5 md:px-0 gap-6 md:gap-8 lg:gap-6.5">
              <Divider />
              <div className="flex flex-row gap-8 ">
                <Container className="flex-grow min-w-0">
                  <MainPropertyInfo />
                  {hasKeyFeatures && (
                    <>
                      <Divider />
                      <KeyFeatures />
                    </>
                  )}
                  <Divider />
                  <Container className="w-full flex lg:hidden">
                    <div className="flex justify-center gap-6">
                      <GoogleAd adSlotName={"PropertyDetailsMPU1"} />
                      <div className="hidden md:block">
                        <GoogleAd adSlotName={"PropertyDetailsMPU2"} />
                      </div>
                    </div>
                    <Divider />
                  </Container>
                  {description && (
                    <TextSection
                      titleText="Property description"
                      as="h2"
                      text={description}
                      headingStyle={FontStyles.h5}
                    />
                  )}
                  {hasRooms && (
                    <>
                      <Divider />
                      <RoomsDescription />
                    </>
                  )}
                  {hasMoreInformation && (
                    <>
                      <Divider />
                      <MoreInformation />
                    </>
                  )}
                  {hasBookViewing && (
                    <>
                      <Divider />
                      <BookViewing />
                    </>
                  )}
                  {hasContactSection && (
                    <>
                      <Divider />
                      <Contact />
                    </>
                  )}
                  {hasUtilitiesSection && (
                    <>
                      <Divider />
                      <Utilities />
                    </>
                  )}
                  {hasSchools && (
                    <>
                      <Divider />
                      <SchoolCatchments />
                    </>
                  )}
                  {hasDistrictStats && (
                    <>
                      <Divider />
                      <DistrictStats />
                    </>
                  )}
                  {rental === false && numericPrice != null && (
                    <>
                      <Divider />
                      <Calculators price={numericPrice} />
                    </>
                  )}
                  {recentlyViewed.length > 0 && (
                    <>
                      <Divider />
                      <RecentlyViewedProperties items={recentlyViewed} />
                    </>
                  )}
                  {hasLinksSection && (
                    <>
                      <Divider />
                      <LinksTable />
                    </>
                  )}
                  <PropertyReferencePrintSection />
                </Container>
                <div className="hidden lg:flex flex-col gap-6.5 flex-none w-[340px]">
                  <GoogleAd adSlotName={"PropertyDetailsMPU1"} className="self-center" />
                  <MarketedBy />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
